import React, { useEffect, useMemo } from 'react';
import { Button, Paper } from '@material-ui/core';
import styled from 'styled-components/macro';
import {
  DoneAllRounded,
  DoneRounded,
  ErrorOutlineRounded,
  HighlightOffRounded,
} from '@material-ui/icons';

import { BookingInfo } from 'store/models/BookingInfo';
import { dashIfEmpty } from 'utils/empty';
import { useDispatch } from 'react-redux';
import { ticketActions } from 'store/actions';
import { canEditTicketSelector } from 'store/selectors/ticket.selector';
import { useSelector } from 'react-redux';
export interface BookingInfoPanelProps {
  isLoading: boolean;
  bookingInfoM?: BookingInfo;
  bookingNoIsWrongFormat?: boolean;
  index: number; // -1 means top left then 0 is the first of the bookings on the bottom
}

const BookingInfoPanel: React.FC<BookingInfoPanelProps> = ({
  bookingNoIsWrongFormat, // might not needed
  index,
  bookingInfoM,
}) => {
  const dispatch = useDispatch();

  const canEdit = useSelector(canEditTicketSelector);

  const errorText: string = useMemo(() => {
    if (!bookingInfoM && !bookingNoIsWrongFormat) return '';
    else if (bookingNoIsWrongFormat) {
      return `Booking no. is wrong format`;
    }
    return bookingInfoM?.errorText || '';
  }, [bookingInfoM, bookingNoIsWrongFormat]);

  useEffect(() => {
    console.debug(`bookingInfoM.fields`, bookingInfoM?.fields);
  }, [bookingInfoM]);

  const containerInfoString = (info): string | any => {
    if (typeof info === 'string') {
      return dashIfEmpty(info);
    } else if (Array.isArray(info)) {
      if (!info.length) {
        return '-';
      } else if (typeof info[0] === 'string') {
        return info.join(', ');
      } else {
        return renderContainerDetail(info);
      }
    }
    return JSON.stringify(info);
  };

  const renderContainerDetail = (value) => {
    if (!Array.isArray(value))
      return (
        <SDetail>
          <SBox>-</SBox>
        </SDetail>
      );
    return (
      <SDetail>
        {value.map((eachCn, i) => (
          <SBlockBox key={`eachCn-${i}`}>
            {Object.keys(eachCn).map((key) => [
              <SBoxCellTitle key={`${key}-key`}>{key}</SBoxCellTitle>,
              <SBoxCell key={`${key}-value`}>
                {containerInfoString(eachCn[key])}
              </SBoxCell>,
            ])}
          </SBlockBox>
        ))}
      </SDetail>
    );
  };

  const handleOnClickApplySplitToTicket = (splitInfo: string) => {
    dispatch(ticketActions.applySplitInfoToTicket(splitInfo));
  };

  /**
   * show apply to ticket button for both top left and bookings' booking nos
   */
  const showApplyToTicket = useMemo(() => canEdit, [canEdit]);

  const renderSplitInfo = (field) => {
    return (
      <SRow className={field.isError ? 'is-error' : ''}>
        <STopic>{field.label}</STopic>
        <SDetailSplit>
          <SBox>
            {field.value}
            {showApplyToTicket && (
              <SUseSplitFromInfoButton
                size="small"
                variant="contained"
                onClick={() =>
                  handleOnClickApplySplitToTicket(field.value)
                }
              >
                Apply to ticket
              </SUseSplitFromInfoButton>
            )}
          </SBox>
        </SDetailSplit>
      </SRow>
    );
  };

  const renderContainer = (field) => {
    return (
      <SRow className={field.isError ? 'is-error' : ''}>
        <STopic>{field.label}</STopic>
        {renderContainerDetail(field.value)}
      </SRow>
    );
  };

  const renderFoundInfo = () => {
    if (!bookingInfoM) return null;
    let IconDiv: any | null = null;
    if (bookingInfoM.isFoundInBoth) {
      IconDiv = <DoneAllRounded />;
    } else if (bookingInfoM.found) {
      IconDiv = <DoneRounded />;
    } else {
      // IconDiv = <HighlightOffRounded />;
      return null;
    }
    return (
      <SFoundInfoWrapper>
        {IconDiv}
        <SFoundInfoText>{bookingInfoM.foundText}</SFoundInfoText>
      </SFoundInfoWrapper>
    );
  };

  const renderContent = () => {
    return !bookingInfoM && !errorText ? null : (
      <SContent>
        <SInfos>
          {errorText && (
            <SBookingNotFound
              className={
                bookingInfoM?.failedFromAnyApi ? 'apiError' : ''
              }
            >
              <SErrorIconWrapper>
                {bookingInfoM?.failedFromAnyApi ? (
                  <SCLTErrorIcon />
                ) : (
                  <SNotFoundIcon />
                )}
              </SErrorIconWrapper>
              {errorText}
            </SBookingNotFound>
          )}
          {renderFoundInfo()}
        </SInfos>
        {bookingInfoM?.found && (
          <SInfoWrapper>
            {bookingInfoM.fields.map((field) => {
              if (field.value && field.useCustomRender) {
                if (field.dataKey === 'container') {
                  return renderContainer(field);
                } else if (field.dataKey === 'splitInfo') {
                  return renderSplitInfo(field);
                }
                return null;
              } else if (!field.subfields) {
                return (
                  <SRow className={field.isError ? 'is-error' : ''}>
                    <STopic>{field.label}</STopic>
                    <SDetail>
                      <SBox>{field.value}</SBox>
                    </SDetail>
                  </SRow>
                );
              } else {
                return (
                  <SRow>
                    <STopic>{field.label}</STopic>
                    <SBigBox>
                      {field.subfields.map((subfield) => (
                        <SSmallBox
                          className={
                            subfield.isError ? 'is-error' : ''
                          }
                        >
                          <SSubSmallBox>
                            {subfield.label}
                          </SSubSmallBox>
                          <SSubSmallBox>
                            {subfield.value}
                          </SSubSmallBox>
                        </SSmallBox>
                      ))}
                    </SBigBox>
                  </SRow>
                );
              }
            })}
          </SInfoWrapper>
        )}
      </SContent>
    );
  };
  if (!bookingInfoM && !errorText) return null;
  return <SPaper>{renderContent()}</SPaper>;
};
export default BookingInfoPanel;
const SPaper = styled(Paper)`
  padding: 1rem;
  z-index: 1;
  font-size: ${(props) => props.theme.typography.fontSize.small};
  max-height: 300px;
  overflow-y: auto;
`;

const STopic = styled.div`
  line-height: 1.8;
  font-size: ${(props) => props.theme.typography.fontSize.extraSmall};
`;
const SDetailSplit = styled.div`
  position: relative;
`;
const SDetail = styled.div``;
const SBox = styled.div`
  display: flex;
  align-items: center;
  background: #f5f5f5;
  border-radius: 5px;
  padding: 0.3rem;
  line-height: 1rem;
  font-size: ${(props) => props.theme.typography.fontSize.small};
`;
const SBlockBox = styled.div`
  background: #f5f5f5;
  border-radius: 5px;
  padding: 0.3rem;
  line-height: 1rem;
  font-size: ${(props) => props.theme.typography.fontSize.small};
  display: grid;
  grid-template-columns: max-content auto;
  column-gap: 5px;
  & + & {
    margin-top: 5px;
  }
  max-height: 10rem;
  overflow: auto;
`;
const SBigBox = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  column-gap: 1rem;
`;
const SSmallBox = styled.div.attrs((props) => ({
  className: props.className,
}))`
  background: #f5f5f5;
  border-radius: 5px;
  &.is-error {
    color: ${(props) => props.theme.palette.primary.main};
  }
  display: grid;
  justify-content: center;
  row-gap: 5px;
  padding: 3px 0;
`;
const SSubSmallBox = styled.div``;
const SBookingNotFound = styled.div`
  color: ${(props) => props.theme.palette.error.main};
  & > svg {
    color: ${(props) => props.theme.palette.error.main};
  }
  padding: 2px 1rem;
  width: fit-content;
  border-radius: 1rem;
  margin-bottom: 3px;
  &:not(.apiError) {
    background-color: ${(props) =>
      props.theme.palette.error.lightRGBA};
  }
  font-weight: ${(props) => props.theme.typography.fontWeight.medium};
  display: flex;
  align-items: center;
`;

const SContent = styled.div``;

const SCLTErrorIcon = styled(ErrorOutlineRounded)``;
const SNotFoundIcon = styled(HighlightOffRounded)``;
const SErrorIconWrapper = styled.div`
  margin-right: 0.5rem;
`;
const SRow = styled.div.attrs((props) => ({
  className: props.className,
}))`
  &.is-error {
    color: ${(props) => props.theme.palette.primary.main};
  }
`;
const SInfoWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 200px));
  overflow-wrap: anywhere;
  column-gap: 0.5rem;
  row-gap: 0.2rem;
`;

const SBoxCell = styled.div``;
const SBoxCellTitle = styled.div`
  font-weight: ${(props) => props.theme.typography.fontWeight.medium};
`;
const SFoundInfoWrapper = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  color: ${(props) => props.theme.palette.success.main};
  background: #81c7841f;
  padding: 2px 1rem;
  width: fit-content;
  border-radius: 1rem;
  margin-bottom: 3px;
`;
const SFoundInfoText = styled.div``;
const SInfos = styled.div`
  display: flex;
  justify-content: space-between;
`;
const SUseSplitFromInfoButton = styled(Button)`
  &.MuiButton-root {
    position: absolute;
    top: 0;
    right: 0;
    font-size: ${(props) => props.theme.typography.fontSize.small};
    border-radius: 1rem;
    padding: 0px 10px;
  }
`;
